import { css, keyframes } from "@emotion/react";
import styled from "@emotion/styled";
import classNames from "classnames";
import type { HTMLAttributes } from "react";

import { SvgInteractLogo } from "@skilitics/designer-theme";

import { Icon } from "../icons";

export type LoadingProps = HTMLAttributes<HTMLDivElement> & {
  fillColor?: string;
  size?: number;
  inline?: boolean;
};

const rotate = keyframes`
  to {
    transform: rotate(360deg);
  }
`;

export const LoadingContainer = styled.div<{ fillColor?: string; spin?: boolean }>`
  position: relative;

  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  color: ${({ fillColor, theme }) => fillColor ?? (theme.type === "dark" ? theme.brandColor : theme.generalTextIcon)};

  &.loadingContainer {
    background-color: ${({ theme }) => theme.surface};
  }

  &.inline {
    width: auto;
    margin: 0 10px 0 0;
  }

  ${({ spin }) =>
    spin &&
    css`
      svg {
        transform-origin: 50% 50%;
        &.off-center {
          transform-origin: 45% 50%;
        }
        animation: ${rotate} 1.2s linear infinite;
      }
    `}
`;

export const LoadingArc = ({ fillColor = "currentColor", size = 60, inline = false, className }: LoadingProps) => (
  <LoadingContainer fillColor={fillColor} className={classNames({ inline })} data-test="loading-container" spin>
    <Icon icon="spinner-third" size={size} className={className} />
  </LoadingContainer>
);

export const LoadingDots = ({ fillColor = "currentColor", size = 60, inline = false }: LoadingProps) => (
  <LoadingContainer fillColor={fillColor} className={classNames({ inline })} data-test="loading-container" spin>
    <Icon icon="spinner" size={size} />
  </LoadingContainer>
);

export { LoadingArc as Loading };

export const LogoLoading = ({ fillColor, inline = false }: LoadingProps) => (
  <LoadingContainer fillColor={fillColor} className={classNames({ inline, loadingContainer: true })}>
    <LogoSpinContainer $size={100}>
      <Circles>
        <Circle $color="red" />
        <Circle $color="orange" />
        <Circle $color="blue" />
      </Circles>
      <LogoReveal>
        <SvgInteractLogo width={55} height={80} />
      </LogoReveal>
    </LogoSpinContainer>
  </LoadingContainer>
);

const circleSpin = keyframes`
  0%, 10% {
    transform: rotate(0deg) scale(1);
    opacity: 1;
  }
  30%, 80% {
    transform: rotate(360deg) scale(0);
    opacity: 0;
  }
  100% {
    transform: rotate(720deg) scale(1);
    opacity: 1;
  }
`;

const logoReveal = keyframes`
  0%, 25% {
    transform: scale(0.3) rotate(-15deg);
    opacity: 0;
  }
  35%, 75% {
    transform: scale(1.1) rotate(0deg);
    opacity: 1;
  }
  85%, 100% {
    transform: scale(0.3) rotate(15deg);
    opacity: 0;
  }
`;

const LogoSpinContainer = styled.div<{ $size: number }>`
  position: relative;
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
`;

const Circles = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  animation: ${circleSpin} 3s ease-in-out infinite;
`;

const Circle = styled.div<{ $color: "red" | "orange" | "blue" }>`
  position: absolute;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  top: 50%;
  left: 50%;
  margin-top: -12px;
  margin-left: -12px;

  background: ${({ $color }) => {
    switch ($color) {
      case "red":
        return "#F0567A";
      case "orange":
        return "#FF8600";
      case "blue":
        return "#009fda";
    }
  }};

  transform: ${({ $color }) => {
    switch ($color) {
      case "red":
        return "translateY(-20px)";
      case "orange":
        return "translate(17px, 10px)";
      case "blue":
        return "translate(-17px, 10px)";
    }
  }};
`;

const LogoReveal = styled.div`
  position: absolute;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: ${logoReveal} 3s ease-in-out infinite;
`;

const LoadingIndicatorContainer = styled(LoadingContainer)`
  color: ${({ theme }) => theme.tertiary};

  transition:
    opacity 300ms ease-in-out,
    visibility 300ms ease-in-out;

  &.hidden {
    opacity: 0;
    visibility: hidden;
  }
`;

export const LoadingIndicator = ({ hidden = false }: { hidden?: boolean }) => {
  return (
    <LoadingIndicatorContainer className={hidden ? "hidden" : "fa-spin"}>
      <Icon icon="circle-notch" />
    </LoadingIndicatorContainer>
  );
};
